import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE, formatDate, formatApiErrorDetail } from "../lib/api";
import SignaturePad from "../components/app/SignaturePad";
import { CheckCircle, ShieldCheck, Download, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PublicDocument() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [signatureData, setSignatureData] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/public/documents/${token}`);
      setDoc(data);
      if (data.cliente_snapshot?.ragione_sociale && !signerName) {
        setSignerName(data.cliente_snapshot.ragione_sociale);
      }
    } catch {
      setDoc(false);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  async function submitSignature(e) {
    e.preventDefault();
    if (!signerName.trim()) return toast.error("Inserisci nome e cognome");
    if (!signatureData) return toast.error("Disegna la firma");
    if (!acceptedTerms) return toast.error("Accetta le condizioni");
    setSigning(true);
    try {
      await axios.post(`${API_BASE}/public/documents/${token}/sign`, {
        firmatario_nome: signerName,
        firma_data_url: signatureData,
        accept_terms: true,
      });
      toast.success("Documento firmato con successo");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore firma");
    } finally {
      setSigning(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F5F7] flex items-center justify-center">
        <div className="text-sm text-[#64748B]">Caricamento documento…</div>
      </div>
    );
  }
  if (!doc) {
    return (
      <div className="min-h-screen bg-[#F4F5F7] flex items-center justify-center p-6">
        <div className="bg-white border border-[#E2E8F0] rounded-md p-10 max-w-md text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D32F2F] mb-3">Errore 404</div>
          <h1 className="font-display text-2xl font-bold mb-2">Documento non trovato</h1>
          <p className="text-sm text-[#64748B]">Il link potrebbe essere scaduto o non valido.</p>
        </div>
      </div>
    );
  }

  const azienda = doc.azienda?.ragione_sociale || "Sa.Gi Group";
  const isSigned = doc.stato === "firmato";
  const pdfUrl = `${API_BASE}/public/documents/${token}/file${isSigned ? "?signed=true" : ""}`;

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      {/* Header */}
      <div className="bg-white border-b-2 border-[#0A0A0A]">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-5 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B]">Documento da firmare</div>
            <div className="font-display text-lg sm:text-xl font-bold truncate">{doc.titolo}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B]">Mittente</div>
            <div className="font-bold text-sm">{azienda}</div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 space-y-6">
        {isSigned && (
          <div className="bg-[#008A3C] text-white p-5 rounded-sm flex items-start gap-3" data-testid="signed-banner">
            <CheckCircle size={24} weight="fill" className="shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold text-sm uppercase tracking-[0.15em]">Documento firmato</div>
              <div className="text-xs mt-1 opacity-90">
                Firmato da <strong>{doc.signed_by_name}</strong> il {doc.signed_at ? formatDate(doc.signed_at) : "—"}
              </div>
            </div>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="download-signed-btn"
              className="bg-white text-[#008A3C] px-4 py-2 text-[10px] uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2 hover:bg-gray-100"
            >
              <Download size={14} weight="bold" /> Scarica PDF firmato
            </a>
          </div>
        )}

        {/* PDF Viewer */}
        <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-hidden">
          <div className="px-4 sm:px-6 py-3 border-b border-[#E2E8F0] flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <FileText size={18} weight="duotone" className="text-[#0033A0] shrink-0" />
              <span className="text-xs uppercase tracking-[0.15em] font-bold truncate">Anteprima Documento</span>
            </div>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="download-original-btn"
              className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#0033A0] hover:underline flex items-center gap-1"
            >
              <Download size={12} weight="bold" /> Apri PDF
            </a>
          </div>
          <iframe
            src={pdfUrl}
            title="Documento"
            data-testid="pdf-iframe"
            className="w-full bg-[#F4F5F7]"
            style={{ height: "70vh", minHeight: 500 }}
          />
        </div>

        {/* Form firma */}
        {!isSigned && (
          <form onSubmit={submitSignature} className="bg-white border border-[#E2E8F0] rounded-sm p-5 sm:p-8 space-y-5" data-testid="signature-form">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#0033A0] mb-2">Firma Digitale</div>
              <h2 className="font-display text-2xl font-bold">Firma il documento</h2>
              <p className="text-sm text-[#64748B] mt-2">
                Per accettare e firmare digitalmente, compila i campi sotto, disegna la tua firma e conferma.
              </p>
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">Nome e Cognome del firmatario *</label>
              <input
                required
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Es. Mario Rossi"
                data-testid="signer-name-input"
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-sm focus:border-[#0033A0] focus:outline-none text-sm"
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">Firma *</label>
              <SignaturePad onChange={setSignatureData} height={200} />
            </div>

            <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="accept-terms">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#0033A0]"
              />
              <span className="text-xs text-[#64748B] leading-relaxed">
                Confermo di aver letto e accettato integralmente il contenuto del documento. La firma apposta ha valore di firma elettronica semplice ed è collegata al firmatario tramite indirizzo IP, timestamp e tracciato grafico.
              </span>
            </label>

            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-[#E2E8F0]">
              <div className="flex items-center gap-2 text-xs text-[#64748B]">
                <ShieldCheck size={16} weight="duotone" className="text-[#008A3C]" />
                Connessione sicura · {azienda}
              </div>
              <button
                type="submit"
                disabled={signing}
                data-testid="submit-signature-btn"
                className="btn-primary px-6 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm disabled:opacity-50"
              >
                {signing ? "Firma in corso…" : "Firma e Conferma →"}
              </button>
            </div>
          </form>
        )}

        <div className="text-center text-[10px] uppercase tracking-[0.3em] text-[#64748B] pt-4">
          Powered by Gestionale 360
        </div>
      </div>
    </div>
  );
}
